import { Lock } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { PlanType } from '@/graphql'
import { usePermissions } from '@/hooks/usePermissions'
import useT from '@/i18n/useT'

interface UpgradeMessageProps {
  message?: string | null
  link?: string
  requiredPlan?: PlanType
}

export const FeatureMessage = ({
  HeaderComponent,
  message,
  onClick,
  buttonText,
  sx,
}: {
  HeaderComponent?: React.ReactNode
  message: string
  onClick?: () => void
  buttonText: string
  sx?: SxProps<Theme>
}) => {
  const theme = useTheme()
  const color = theme.palette.info.main

  return (
    <Box
      sx={{
        position: 'relative',
        p: 3,
        background:
          'linear-gradient(135deg, rgb(2, 136, 209, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%)',
        border: '1px solid',
        borderColor: `${color}aa`,
        borderRadius: 2,
        textAlign: 'center',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${color}, ${color}aa)`,
        },
        ...sx,
      }}
    >
      {HeaderComponent}
      <Typography
        variant={'body1'}
        component="p"
        sx={{
          fontWeight: 500,
          mb: 2,
        }}
      >
        {message}
      </Typography>
      {onClick && (
        <Button
          variant="contained"
          onClick={onClick}
          sx={{
            background: color,
            color: 'white',
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          {buttonText}
        </Button>
      )}
    </Box>
  )
}

const UpgradeMessage: React.FC<UpgradeMessageProps> = ({
  message,
  link = '/pricing',
  requiredPlan = PlanType.PREMIUM,
}) => {
  const { t } = useT()
  const theme = useTheme()
  const color = theme.palette.info.main
  const { permissions } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <FeatureMessage
      message={
        message ||
        t('upgradeMessage.defaultMessage', {
          requiredPlan: requiredPlan || 'a higher plan',
        })
      }
      onClick={
        link
          ? () => navigate(link, { state: { from: location.pathname } })
          : undefined
      }
      buttonText={
        !permissions.CAN_MANAGE_BILLING
          ? t('upgradeMessage.button.viewPricing')
          : t('upgradeMessage.button.upgrade')
      }
      HeaderComponent={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {requiredPlan && (
            <Chip
              icon={<Lock sx={{ fontSize: 20 }} />}
              label={t('upgradeMessage.requiredPlan', {
                requiredPlan,
              })}
              size="small"
              sx={{
                bgcolor: `${color}20`,
                color,
                border: `1px solid ${color}40`,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            />
          )}
        </Box>
      }
    />
  )
}

export default UpgradeMessage
